import { useTranslation } from 'next-i18next';
import Link from 'next/link';
import React, { useEffect, useMemo } from 'react';
import { RouteE } from 'src/constants/pages.constants';
import { useFetchArtistsByFilter } from 'src/features/common/artist/artist.hooks';
import { CreatorCard } from 'src/features/common/universe/components/creator-card';
import { useUniverse } from 'src/features/common/universe/hooks/use-universe';
import { Button } from 'src/ui/common/atoms/button';
import { Typography } from 'src/ui/common/atoms/typography';
import { LandingSection } from 'src/ui/common/layouts/landing-section';
import { StyledCarousel, StyledEmptyControl, StyledEmptyList } from './styles';

export const CreatorsMainContainer: React.VFC<{ title?: string }> = ({ title }) => {
    const { t } = useTranslation(['components', 'pages']);
    const { isUniverseOwner, ownerId } = useUniverse();
    const { artists, fetchArtists, isLoading } = useFetchArtistsByFilter({
        ownerId: ownerId!,
    });

    const itemsGridConfig = useMemo(
        () => ({
            xs: 2,
            sm: 3,
            md: 4,
            lg: 5,
            xl: 5,
            columnGap: 20,
            columnGapLg: 20,
        }),
        []
    );

    useEffect(() => {
        ownerId && fetchArtists();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ownerId]);

    if (!isLoading && !artists?.length && !isUniverseOwner) {
        return null;
    }

    return (
        <LandingSection title={title || t('creators.title', { ns: 'components' })}>
            {!isLoading && !artists?.length && isUniverseOwner && (
                <StyledEmptyList itemsGridConfig={itemsGridConfig}>
                    <Typography size="base" variant="title" tag="span">
                        {t('creators.empty', { ns: 'components' })}
                    </Typography>
                    <StyledEmptyControl>
                        <Link href={RouteE.UNIVERSE_CREATORS}>
                            <Button variant="white">{t('creators.manage.title', { ns: 'pages' })}</Button>
                        </Link>
                    </StyledEmptyControl>
                </StyledEmptyList>
            )}
            {!isLoading && !!artists?.length && (
                <StyledCarousel
                    xl={5}
                    lg={4.2}
                    md={3.2}
                    sm={2}
                    slidesToShow={2}
                    slidesToScroll={2}
                    centerPadding="0px"
                    autoplay={true}
                    autoplaySpeed={3000}
                    infinite={true}
                    initialSlide={0}
                    variableWidth
                    centerMode
                >
                    {artists?.map((artist) => (
                        <CreatorCard key={artist.id} data={artist} />
                    ))}
                </StyledCarousel>
            )}
        </LandingSection>
    );
};